import type {
  HotelBookableRoom,
  CancellationPolicy,
  SimplifiedPax,
  TaxesAndFee,
  HotelBookableOption,
} from '@libs/trip-planner-interfaces';

import { assertSmartSearchValue, assertSmartSearchList } from '../../utils';

import { toMoney } from './moneyResponseQL';
import type { HotelIngredientResponseSuggestedQL } from './hotelIngredientResponseQL';
import type { ProductAvailabilityResponseQL } from './productAvailabilityResponseQL';
import { toBoardBasisCode } from './boardBasisCodeQL';

type HotelBookableOptionQL =
  | HotelIngredientResponseSuggestedQL
  | ProductAvailabilityResponseQL;
export function toHotelBookableOption(
  bookableOptionQL: HotelBookableOptionQL
): HotelBookableOption {
  assertSmartSearchValue(bookableOptionQL.totalPrice);

  return {
    addToBasketToken: bookableOptionQL.addToBasketToken,
    code: bookableOptionQL.code,
    totalPrice: toMoney(bookableOptionQL.totalPrice),
    isRefundable: bookableOptionQL.isRefundable,
  };
}

type RoomsQL = NonNullable<HotelBookableOptionQL['rooms']>;
type RoomQL = NonNullable<RoomsQL[number]>;
export function toHotelBookableRoom(
  suggestedRoomQL: RoomQL
): HotelBookableRoom {
  assertSmartSearchValue(suggestedRoomQL.boardBasis);
  assertSmartSearchList(suggestedRoomQL.cancellationPolicies);
  assertSmartSearchList(suggestedRoomQL.pax);
  assertSmartSearchValue(suggestedRoomQL.price);
  assertSmartSearchList(suggestedRoomQL.taxesAndFees);

  return {
    boardBasis: toBoardBasisCode(suggestedRoomQL.boardBasis),
    cancellationPolicies:
      suggestedRoomQL.cancellationPolicies.map(toCancellationPolicy),
    description: suggestedRoomQL.description,
    pax: suggestedRoomQL.pax.map(toSimplifiedPax),
    price: toMoney(suggestedRoomQL.price),
    taxesAndFees: suggestedRoomQL.taxesAndFees.map(toTaxAndFee),
  };
}

type CancellationPoliciesQL = NonNullable<RoomQL['cancellationPolicies']>;
type CancellationPolicyQL = NonNullable<CancellationPoliciesQL[number]>;
function toCancellationPolicy(
  cancellationPolicyQL: CancellationPolicyQL
): CancellationPolicy {
  assertSmartSearchValue(cancellationPolicyQL.amount);

  return {
    amount: toMoney(cancellationPolicyQL.amount),
    fromDate: cancellationPolicyQL.fromDate,
    fromDateIso: cancellationPolicyQL.fromDateIso,
  };
}

type PaxQL = NonNullable<RoomQL['pax']>[number];
function toSimplifiedPax(paxQL: PaxQL): SimplifiedPax {
  return {
    age: paxQL?.age ?? null,
  };
}

type TaxesAndFeesQL = NonNullable<RoomQL['taxesAndFees']>;
type TaxAndFeeQL = NonNullable<TaxesAndFeesQL[number]>;
function toTaxAndFee(taxAndFeeQL: TaxAndFeeQL): TaxesAndFee {
  assertSmartSearchValue(taxAndFeeQL.amount);

  return {
    amount: toMoney(taxAndFeeQL.amount),
    isIncludedInPrice: taxAndFeeQL.isIncludedInPrice,
    type: taxAndFeeQL.type,
  };
}
